import { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback, useRef } from "react";
import moment from "moment";
import { useParams } from "react-router";
import { useHistory, useLocation } from "react-router-dom";
import { useCookieState } from "cookie-state";
import { authorization } from "module/auth";
import { User } from "module/auth/dto";
import { CountryType } from "store/types";
import { useQuery } from "react-query";
import { apiClient } from "module/api";
import { ADMCampaignData, Audience, CampaignAudience } from "campaign-builder/domain/types";
import { getIndexOfCurrentStep, isAheadByBusinessDays, parseTimestampStringDate } from "campaign-builder/domain/utils";
import { CAMPAIGN_INTERNAL_STATUSES } from "pages/constants";
import { getCampaign, getCountryData, getAdmCampaignSelfServeData } from "../../graphQL";
import { resolveBuilderChannel } from "campaign-builder/config";
import { ADM_STEP, MINIMUM_BUSINESS_DAYS } from "campaign-builder/domain/constants";
import { useCopilotSync } from "campaign-builder/hooks/useCopilotSync";
import { motherCampaignIdOf, motherRedirectPath } from "campaign-builder/motherCampaign";
import CONFIG from "config/config";

interface CampaignDataContextProps {
  user: User;
  country: CountryType | null;
  activeAudienceDetails: Audience[];
  activeTabIndex: number;
  campaignData: ADMCampaignData | null;
  updateCampaignState: (campaignState: ADMCampaignData) => void;
  error: Error | null;
  isLoading: boolean;
  refetchCampaign: () => Promise<unknown>;
  setActiveTabIndex: (index: number) => void;
  setCampaignId: (campaignId: string) => void;
  setClientId: (clientId: string) => void;
  campaignId: string | null;
  clientId: string | null;
  audiences: Audience[];
  audiencesError: Error | null;
  audiencesLoading: boolean;
  refetchAudiences: () => void;
  loadMoreAudiences: () => Promise<void>;
  hasMoreAudiences: boolean;
  isLoadingMoreAudiences: boolean;
  campaignAudiences: CampaignAudience[];
  campaignAudiencesError: Error | null;
  campaignAudiencesLoading: boolean;
  refetchCampaignAudiences: () => void;
  shouldDisplayDatesAlert: boolean;
  isReadOnly: boolean;
  setIsReadOnly: (isReadOnly: boolean) => void;
  disableFollowingHeaderSteps: boolean;
  setDisableFollowingHeaderSteps: (disableFollowingHeaderSteps: boolean) => void;
  isSideBarOpen: boolean;
  setIsSideBarOpen: (isSideBarOpen: boolean) => void;
  isRedirectingToAudience: boolean;
  setIsRedirectingToAudience: (value: boolean) => void;
  recipientsAmount: number;
  variables: any[];
  variablesError: Error | null;
  variablesLoading: boolean;
  refetchVariables: () => void;
  campaignVariables: any[];
  campaignVariablesError: Error | null;
  campaignVariablesLoading: boolean;
  refetchCampaignVariables: () => void;
}

const CampaignDataContext = createContext<CampaignDataContextProps | undefined>(undefined);

// Export context ref for safe access from outside the provider (e.g. Variables panel)
export const CampaignDataContextRef = CampaignDataContext;
const stepToIndex = {
  [ADM_STEP.SEGMENTS]: 0,
  [ADM_STEP.DESIGN]: 1,
  [ADM_STEP.SUBMIT]: 2,
};
export const CampaignDataProvider = ({ children }: { children: ReactNode }) => {
  const params = useParams<any>();
  const [clientId, setClientId] = useState<string | null>(null);
  const [country, setCoutry] = useState<CountryType | null>(null);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(stepToIndex[params.step] || 0);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [campaignState, setCampaignState] = useState<ADMCampaignData | null>(null);
  const [disableFollowingHeaderSteps, setDisableFollowingHeaderSteps] = useState(false);
  const { value: uiData, setValue: setUiData } = useCookieState(
    "ui_data",
    {
      isSidebarOpen: true,
    },
    {
      domain: process.env.NODE_ENV === "development" ? "localhost" : ".oppizi.com",
      path: "/",
      sameSite: "strict",
    }
  );
  const isSideBarOpen = uiData?.isSidebarOpen ?? true;
  const setIsSideBarOpen = (newValue: boolean) => setUiData((prev: any) => ({ ...prev, isSidebarOpen: newValue }));
  const [isRedirectingToAudience, setIsRedirectingToAudience] = useState(false);
  const [allAudiences, setAllAudiences] = useState<Audience[]>([]);
  const [hasMoreAudiences, setHasMoreAudiences] = useState(true);
  const [isLoadingMoreAudiences, setIsLoadingMoreAudiences] = useState(false);
  const currentAudiencesOffset = useRef(0);
  const user = useMemo(() => authorization.getUser(), [authorization]);
  const history = useHistory();
  const { pathname, search } = useLocation();

  const { error, isLoading, refetch } = useQuery<ADMCampaignData | null>({
    queryKey: ["campaign", clientId, campaignId],
    // A child campaign's refusal is an answer, not a transient failure: redirect at once
    retry: (failureCount, queryError) => !motherCampaignIdOf(queryError) && failureCount < 3,
    // Clients never open a child ADM campaign: land on the same builder page of its mother
    onError: (queryError) => {
      const motherId = motherCampaignIdOf(queryError);
      if (!motherId || !campaignId) return;
      history.replace(`${motherRedirectPath(pathname, campaignId, motherId)}${search}`);
    },
    queryFn: async () => {
      if (!campaignId || !clientId) {
        return null;
      }
      const campaign: ADMCampaignData = await getCampaign(campaignId, clientId);
      campaign.isSubmitted =
        !!campaign.internalStatus &&
        campaign.internalStatus !== CAMPAIGN_INTERNAL_STATUSES.DRAFT &&
        campaign.internalStatus !== CAMPAIGN_INTERNAL_STATUSES.REJECTED;

      campaign.id = Number(campaignId);
      const savedStartDate = campaign.startDate
        ? new Date(
            moment(moment(Number(campaign.startDate)).utc().format().split("T")[0], "YYYY-MM-DD", true).format()
          ).getTime()
        : "";

      campaign.startDate = String(savedStartDate);

      // H2H/D2D have no ADM self-serve record; fetching it throws and blanks the
      // builder. Only ADM/EDDM carry the address-on-move + submitted cost breakdown.
      const builderChannel = resolveBuilderChannel({ channel: campaign.channel, subtype: campaign.subtype });
      const isStaticChannel = builderChannel === "H2H" || builderChannel === "D2D";

      let parsedCampaign = { ...campaign };
      if (!isStaticChannel) {
        const selfServeCampaignData = await getAdmCampaignSelfServeData(campaignId);
        campaign.updateAddressOnMove = selfServeCampaignData?.updateAddressOnMove;
        parsedCampaign = { ...campaign };
        if (campaign.isSubmitted) {
          setIsReadOnly(true);
          const submittedDetailedCosts = selfServeCampaignData?.campaignDetailedCosts;
          parsedCampaign = {
            ...campaign,
            taxes: submittedDetailedCosts.tax,
            subtotal: submittedDetailedCosts.subtotal,
            totalCosts: submittedDetailedCosts.totalCost,
            amountOff: submittedDetailedCosts.amountOff,
            percentOff: submittedDetailedCosts.percentOff,
          };
        }
      } else if (campaign.isSubmitted) {
        setIsReadOnly(true);
      }
      setCampaignState(parsedCampaign);
      return parsedCampaign;
    },
  });

  // Hide the refusal from the builder while onError redirects, so the error state never flashes
  const motherCampaignId = motherCampaignIdOf(error);

  const {
    data: campaignAudiences = [],
    error: campaignAudiencesError,
    isLoading: campaignAudiencesLoading,
    refetch: refetchCampaignAudiences,
  } = useQuery<CampaignAudience[]>({
    queryKey: ["campaignAudiences", campaignId],
    queryFn: async () => {
      if (!campaignId) {
        return [];
      }
      const campaignAudiencesResponse = await apiClient.getCampaignAudiences(campaignId);
      if (campaignAudiencesResponse.status !== 200) {
        return [];
      }
      const fetchedCampaignAudiences = await campaignAudiencesResponse.json();
      return fetchedCampaignAudiences;
    },
  });

  const {
    data: campaignVariables = [],
    error: campaignVariablesError,
    isLoading: campaignVariablesLoading,
    refetch: refetchCampaignVariables,
  } = useQuery<any[]>({
    queryKey: ["campaignVariables", campaignId],
    queryFn: async () => {
      if (!campaignId) {
        return [];
      }
      try {
        const campaignVariablesResponse = await apiClient.getCampaignVariables(campaignId);
        if (campaignVariablesResponse.status !== 200) {
          return [];
        }
        const fetchedCampaignVariables = await campaignVariablesResponse.json();
        return fetchedCampaignVariables;
      } catch (error) {
        console.error("Error fetching campaign variables:", error);
        return [];
      }
    },
  });

  // Extract variable IDs from campaignVariables to use as query dependency
  // This ensures the variables query re-runs after campaignVariables is loaded
  const campaignVariableIds = campaignVariables.map((cv: any) => cv.variable?.id).filter(Boolean);

  const {
    data: variables = [],
    error: variablesError,
    isLoading: variablesLoading,
    refetch: refetchVariables,
  } = useQuery<any[]>({
    queryKey: ["variables", clientId, campaignId, campaignVariableIds],
    queryFn: async () => {
      if (!clientId) {
        return [];
      }

      let selectedVariables: any[] = [];
      if (campaignId && campaignVariables.length > 0) {
        const variableIds = campaignVariables.map((cv: any) => cv.variable?.id).filter(Boolean);
        if (variableIds.length > 0) {
          // Fetch selected variables by IDs if needed
          selectedVariables = await Promise.all(
            variableIds.map(async (id: number) => {
              try {
                const response = await fetch(`${CONFIG.API_BASE_URL}/variable/${id}`, {
                  headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "x-api-key": authorization.getAPIKey(),
                  },
                });
                if (response.status === 200) {
                  return await response.json();
                }
              } catch (error) {
                console.error(`Error fetching variable ${id}:`, error);
              }
              return null;
            })
          );
          selectedVariables = selectedVariables.filter(Boolean);
        }
      }

      const selectedIds = new Set(selectedVariables.map((v: any) => v.id));
      const limit = 50;
      let offset = 0;
      let allVariables: any[] = [...selectedVariables];

      while (allVariables.length < limit) {
        // Fetch only unassigned variables
        const batch = await apiClient.getClientVariables(clientId, offset, limit, {
          unassigned: true,
        });
        if (batch.length === 0) {
          break;
        }

        const uniqueBatch = batch.filter((v: any) => !selectedIds.has(v.id) && !v.deleted_at);
        allVariables = [...allVariables, ...uniqueBatch];

        if (batch.length < limit || allVariables.length >= limit) {
          break;
        }

        offset += limit;
      }

      return allVariables.slice(0, limit);
    },
    enabled: !!clientId,
  });

  const {
    data: initialAudiences = [],
    error: audiencesError,
    isLoading: audiencesLoading,
    refetch: refetchAudiences,
  } = useQuery<Audience[]>({
    queryKey: ["audiences", clientId, campaignId],
    queryFn: async () => {
      if (!clientId) {
        return [];
      }

      let selectedAudiences: Audience[] = [];
      if (campaignId) {
        try {
          const campaignAudiencesResponse = await apiClient.getCampaignAudiences(campaignId);
          if (campaignAudiencesResponse.status === 200) {
            const fetchedCampaignAudiences = await campaignAudiencesResponse.json();
            if (fetchedCampaignAudiences.length > 0) {
              const audienceIds = fetchedCampaignAudiences.map((ca: CampaignAudience) => ca.audience_id);
              selectedAudiences = await apiClient.getAudiencesByIds(audienceIds);
            }
          }
        } catch (error) {
          console.error("Error fetching selected audiences:", error);
        }
      }

      const selectedIds = new Set(selectedAudiences.map((a) => a.id));
      const limit = 50;
      let offset = 0;
      let allAudiences: Audience[] = [...selectedAudiences];
      let totalAudiencesRequested = 0;

      while (allAudiences.length < limit) {
        const batch = await apiClient.getClientAudiences(clientId, offset, limit);
        if (batch.length === 0) {
          break;
        }

        totalAudiencesRequested += batch.length;
        const uniqueBatch = batch.filter((a) => !selectedIds.has(a.id));
        allAudiences = [...allAudiences, ...uniqueBatch];

        if (batch.length < limit || allAudiences.length >= limit) {
          break;
        }

        offset += limit;
      }

      currentAudiencesOffset.current = totalAudiencesRequested;
      return allAudiences.slice(0, limit);
    },
    enabled: !!clientId,
  });

  useEffect(() => {
    if (initialAudiences.length > 0) {
      setAllAudiences((prev) => {
        if (prev.length > 50 && initialAudiences.length === 50) {
          const existingIds = new Set(prev.slice(50).map((a) => a.id));
          const newInitialAudiences = initialAudiences.filter((a) => !existingIds.has(a.id));
          return [...newInitialAudiences, ...prev.slice(50)];
        }
        return initialAudiences;
      });
      setHasMoreAudiences(initialAudiences.length === 50);
    } else if (initialAudiences.length === 0 && !audiencesLoading) {
      setAllAudiences([]);
      setHasMoreAudiences(false);
      currentAudiencesOffset.current = 0;
    }
  }, [initialAudiences, audiencesLoading]);

  const handleRefetchAudiences = useCallback(async () => {
    currentAudiencesOffset.current = 0;
    await refetchAudiences();
  }, [refetchAudiences]);

  const loadMoreAudiences = useCallback(async () => {
    if (!clientId || isLoadingMoreAudiences || !hasMoreAudiences) {
      return;
    }

    setIsLoadingMoreAudiences(true);
    try {
      const limit = 50;
      const currentOffset = currentAudiencesOffset.current;
      const newAudiences = await apiClient.getClientAudiences(clientId, currentOffset, limit);

      if (newAudiences.length > 0) {
        setAllAudiences((prev) => {
          const existingIds = new Set(prev.map((a) => a.id));
          const uniqueNewAudiences = newAudiences.filter((a) => !existingIds.has(a.id));
          return [...prev, ...uniqueNewAudiences];
        });
        currentAudiencesOffset.current = currentOffset + newAudiences.length;
        setHasMoreAudiences(newAudiences.length === limit);
      } else {
        setHasMoreAudiences(false);
      }
    } catch (error) {
      console.error("Error loading more audiences:", error);
    } finally {
      setIsLoadingMoreAudiences(false);
    }
  }, [clientId, isLoadingMoreAudiences, hasMoreAudiences]);

  const audiences = allAudiences;

  const activeAudienceDetails = useMemo(() => {
    const campaignInternalStatus = campaignState?.internalStatus;

    const filteredAudiences = audiences.filter((audience) => {
      if (campaignInternalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT) {
        return !audience.deletedAt;
      }

      const isSelected = campaignAudiences.some((ca) => Number(ca.audience_id) === Number(audience.id));
      if (audience.deletedAt && !isSelected) {
        return false;
      }
      return true;
    });

    return campaignAudiences
      .map((campaignAudience) =>
        filteredAudiences.find((audience) => Number(audience.id) === Number(campaignAudience.audience_id))
      )
      .filter(Boolean) as Audience[];
  }, [campaignAudiences, audiences, campaignState?.internalStatus]);

  const recipientsAmount = useMemo(
    () =>
      activeAudienceDetails
        ? activeAudienceDetails.reduce((acc, audience) => acc + audience.metadata.rowsAmount, 0)
        : 0,
    [activeAudienceDetails]
  );

  useEffect(() => {
    if (campaignState && !activeTabIndex) {
      setActiveTabIndex(getIndexOfCurrentStep(campaignState.lastActiveStep));
    }
  }, [campaignState?.lastActiveStep]);

  const shouldDisplayDatesAlert = useMemo(() => {
    return (
      !!campaignState?.startDate &&
      !isAheadByBusinessDays(parseTimestampStringDate(campaignState.startDate)!, MINIMUM_BUSINESS_DAYS)
    );
  }, [campaignState]);

  useEffect(() => {
    if (!campaignState?.country?.id) return;
    const getAndSaveCountry = async () => {
      const countryId = campaignState.country.id;
      const country = await getCountryData(countryId);
      if (!country) return;
      setCoutry(country);
    };
    getAndSaveCountry();
  }, [campaignState]);

  // client-copilot-v2: when the chat agent changes this campaign via the
  // Oppizi MCP, refetch what it touched so the wizard updates live.
  useCopilotSync(campaignId, {
    refetchCampaign: refetch,
    refetchAudiences,
    refetchCampaignAudiences,
    refetchVariables,
    refetchCampaignVariables,
  });

  return (
    <CampaignDataContext.Provider
      value={{
        country,
        user,
        activeTabIndex,
        campaignData: campaignState,
        updateCampaignState: setCampaignState,
        error: motherCampaignId ? null : (error as Error | null),
        isLoading: isLoading || !!motherCampaignId,
        refetchCampaign: refetch,
        setActiveTabIndex,
        setCampaignId,
        setClientId,
        campaignId,
        clientId,
        refetchAudiences: handleRefetchAudiences,
        audiencesLoading,
        audiences,
        audiencesError: audiencesError as Error | null,
        loadMoreAudiences,
        hasMoreAudiences,
        isLoadingMoreAudiences,
        campaignAudiences,
        campaignAudiencesError: campaignAudiencesError as Error | null,
        campaignAudiencesLoading,
        refetchCampaignAudiences,
        activeAudienceDetails,
        shouldDisplayDatesAlert,
        isReadOnly,
        setIsReadOnly,
        disableFollowingHeaderSteps,
        setDisableFollowingHeaderSteps,
        isSideBarOpen,
        setIsSideBarOpen,
        isRedirectingToAudience,
        setIsRedirectingToAudience,
        recipientsAmount,
        variables,
        variablesError: variablesError as Error | null,
        variablesLoading,
        refetchVariables,
        campaignVariables,
        campaignVariablesError: campaignVariablesError as Error | null,
        campaignVariablesLoading,
        refetchCampaignVariables,
      }}
    >
      {children}
    </CampaignDataContext.Provider>
  );
};

export const useCampaignData = () => {
  const context = useContext(CampaignDataContext);
  if (context === undefined) {
    throw new Error("useCampaignData must be used within a CampaignDataProvider");
  }
  return context;
};
